import { ReactNode } from "react";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./ScheduleOptionCards.module.scss";

export interface ScheduleOptionCard<TValue extends string> {
    value: TValue;
    title: string;
    description: string;
    isDisabled?: boolean;
    /** Beside the title, e.g. a "Recommended" tag. */
    tag?: ReactNode;
    /** Shown under the card, outside the radio, so it can hold a link. */
    hint?: ReactNode;
}

interface ScheduleOptionCardsProps<TValue extends string> {
    ariaLabel: string;
    options: Array<ScheduleOptionCard<TValue>>;
    value: TValue | null;
    onChange: (value: TValue) => void;
    /** Extra inputs for the selected option, e.g. an account picker. */
    renderSelectedExtra?: (value: TValue) => ReactNode;
}

/**
 * Single-choice cards for the schedule lifecycle dialogs. Anything
 * interactive beyond the card sits beside the radio, not inside it: a
 * combobox or link nested in a keyboard-handling radio would lose Space and
 * Enter to the card.
 */
export const ScheduleOptionCards = <TValue extends string>({
    ariaLabel,
    options,
    value,
    onChange,
    renderSelectedExtra,
}: ScheduleOptionCardsProps<TValue>) => (
    <VStack gap={8} role="radiogroup" aria-label={ariaLabel}>
        {options.map((option) => {
            const isSelected = option.value === value;
            const extra = isSelected
                ? renderSelectedExtra?.(option.value)
                : null;
            return (
                <div
                    key={option.value}
                    className={styles.card}
                    data-selected={isSelected || undefined}
                    data-disabled={option.isDisabled || undefined}
                >
                    <button
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        disabled={option.isDisabled}
                        className={styles.cardButton}
                        onClick={() => onChange(option.value)}
                    >
                        {option.tag ? (
                            <span className={styles.titleRow}>
                                <span className={styles.title}>
                                    {option.title}
                                </span>
                                {option.tag}
                            </span>
                        ) : (
                            <span className={styles.title}>{option.title}</span>
                        )}
                        <span className={styles.description}>
                            {option.description}
                        </span>
                    </button>
                    {option.hint ? (
                        <div className={styles.hint}>{option.hint}</div>
                    ) : null}
                    {extra ? <div className={styles.extra}>{extra}</div> : null}
                </div>
            );
        })}
    </VStack>
);
